<script setup lang="ts">
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { AudioLines, KeyRound, LockKeyhole, Music2, ShieldCheck, Sparkles } from "@lucide/vue";
import { api } from "@/lib/api";

const route = useRoute();
const router = useRouter();
const username = ref("admin");
const password = ref("");
const loading = ref(false);
const error = ref("");

async function login() {
  if (!username.value || !password.value || loading.value) return;
  loading.value = true;
  error.value = "";
  try {
    await api.login(username.value, password.value);
    const destination = typeof route.query.redirect === "string"
      && route.query.redirect.startsWith("/")
      ? route.query.redirect
      : "/";
    await router.replace(destination);
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "登录失败，请稍后重试。";
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <main class="login-page">
    <div class="login-grid" aria-hidden="true" />
    <section class="login-story">
      <router-link class="login-brand" to="/login">
        <span class="brand-mark"><Music2 :size="20" /></span>
        <span><strong>声浪工坊</strong><small>WAVECRAFT MUSIC STUDIO</small></span>
      </router-link>
      <div class="login-story-copy">
        <span class="login-kicker"><AudioLines :size="15" /> PRIVATE CREATIVE SPACE</span>
        <h1>把灵感，变成<br /><em>真正能发行的声音。</em></h1>
        <p>从 AI 作词、作曲到母带与发行资料，在你的独立服务器里完成整条创作链路。</p>
      </div>
      <div class="login-trust">
        <span><ShieldCheck :size="17" /> 私有部署</span>
        <span><LockKeyhole :size="17" /> 加密会话</span>
        <span><Sparkles :size="17" /> MiniMax Music 3.0</span>
      </div>
    </section>

    <section class="login-panel-wrap">
      <form class="login-panel" @submit.prevent="login">
        <div class="login-panel-icon"><KeyRound :size="22" /></div>
        <p class="eyebrow">STUDIO ACCESS</p>
        <h2>欢迎回来</h2>
        <p class="login-intro">登录你的私人音乐创作与经营控制台</p>

        <label class="field login-field">
          <span>管理员账号</span>
          <input v-model.trim="username" autocomplete="username" autofocus placeholder="输入账号" />
        </label>
        <label class="field login-field">
          <span>管理员密码</span>
          <input v-model="password" type="password" autocomplete="current-password" placeholder="输入密码" />
        </label>
        <p v-if="error" class="login-error">{{ error }}</p>
        <button class="button button-lime login-submit" :disabled="loading || !username || !password">
          {{ loading ? "正在进入工作室…" : "进入声浪工坊" }}
        </button>
        <p class="login-security"><ShieldCheck :size="14" /> 密码仅在服务器验证，不会写入浏览器存储</p>
      </form>
    </section>
  </main>
</template>
